import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTabsModule } from '@angular/material/tabs';
import { Store } from '@ngrx/store';

import { ActivatedRoute, Router } from '@angular/router';
import { LoadingComponent } from '@components';
import { Status } from '@enums';
import {
  Contract,
  CriminalRecordCheckDocument,
  DocumentType,
  User,
} from '@models';
import { UserService } from '@services';
import {
  createCarTrackProfile,
  fetchCurrentUser,
  fetchDocumentTypes,
  fetchLatestContract,
  fetchLatestCriminalRecordCheckDocument,
  selectContract,
  selectCriminalRecordCheckDocument,
  selectCurrentUser,
  selectDocumentTypes,
  selectUsersFetchedAt,
} from '@state';
import { UserAddressComponent } from './address/address.component';
import { UserBankingDetailsComponent } from './banking-details/banking-details.component';
import { UserContractComponent } from './contract/contract.component';
import { UserCriminalRecordCheckComponent } from './criminal-record-check/criminal-record-check.page';
import { UserDocumentsComponent } from './documents/documents.component';
import { UserDrivingBehaviorComponent } from './driving-behavior/driving-behavior.component';
import { UserExpensesComponent } from './expenses/expenses.component';
import { UserInspectionsComponent } from './inspections/inspections.component';
import { UserProfileComponent } from './profile/profile.component';
import { UserStatementsComponent } from './statements/statements.component';
import { TripsComponent } from './trips/trips.component';
import { UserVehicleComponent } from './vehicle/vehicle.component';

@Component({
  selector: 'app-user',
  standalone: true,
  imports: [
    FormsModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    CommonModule,
    MatIconModule,
    MatInputModule,
    MatButtonModule,
    MatCardModule,
    MatTabsModule,
    MatGridListModule,
    UserProfileComponent,
    UserAddressComponent,
    UserDocumentsComponent,
    UserStatementsComponent,
    UserVehicleComponent,
    TripsComponent,
    UserDrivingBehaviorComponent,
    UserInspectionsComponent,
    UserExpensesComponent,
    UserContractComponent,
    LoadingComponent,
    UserBankingDetailsComponent,
    UserCriminalRecordCheckComponent,
  ],
  templateUrl: './user.page.html',
  styleUrl: './user.page.scss',
})
export class UserPage implements OnInit {
  public $user = this.store.select(selectCurrentUser);
  public $usersFetched = this.store.select(selectUsersFetchedAt);

  public currentTab: string = 'profile';
  public approvedOnlyTabs: string[] = [
    'trips',
    'driver-behavior',
    'inspections',
  ];
  public contract: Contract | null = null;
  public criminalRecordCheckDocument: CriminalRecordCheckDocument | null = null;
  private documentTypes: DocumentType[] = [];
  private user: User | null = null;
  public statementId: string | null = null;

  constructor(
    private store: Store,
    private route: ActivatedRoute,
    private router: Router
  ) {
    this.route.queryParams.subscribe((params) => {
      if (params['tab']) this.currentTab = params['tab'];
      if (params['statementId']) this.statementId = params['statementId'];
    });
  }

  ngOnInit(): void {
    this.store.dispatch(fetchCurrentUser());
    this.store.dispatch(fetchDocumentTypes());
    this.store.dispatch(fetchLatestContract());
    this.store.dispatch(fetchLatestCriminalRecordCheckDocument());
    this.store.select(selectDocumentTypes).subscribe((documentTypes) => {
      this.documentTypes = documentTypes;
    });
    this.store.select(selectContract).subscribe((contract) => {
      this.contract = contract;
    });
    this.store
      .select(selectCriminalRecordCheckDocument)
      .subscribe((document) => {
        this.criminalRecordCheckDocument = document;
      });
    this.$user.subscribe((user: User | undefined) => {
      if (user) {
        this.user = user;
      }
    });
  }

  public statementClicked(statementId: string): void {
    this.statementId = statementId;
    if (!this.statementId) return;
    this.router.navigate(['/users', this.user?.id], {
      queryParams: { tab: this.currentTab, statementId },
    });
  }

  public setTab(tab: string): void {
    if (this.approvedOnlyTabs.includes(tab) && !this.user?.vehicle) {
      return;
    }

    this.router.navigate([], { queryParams: { tab } });
  }

  public onCreateCarTrackProfile(): void {
    if (this.user)
      this.store.dispatch(createCarTrackProfile({ user: this.user }));
  }

  public isUserApproved(user: User): boolean {
    return UserService.isUserApproved(
      user,
      this.documentTypes,
      this.contract,
      this.criminalRecordCheckDocument
    );
  }

  public isProfileApproved(user: User): boolean {
    return UserService.isUserProfileApproved(user);
  }

  public isAddressApproved(user: User): boolean {
    return UserService.isUserAddressApproved(user);
  }

  public areDocumentsApprovedAndValid(user: User): boolean {
    return UserService.areUserDocumentsApproved(user, this.documentTypes);
  }

  public hasCartTrackAccount(user: User): boolean {
    return !!user?.carTrackDriverId;
  }

  public hasAssignedApproveVehicle(user: User): boolean {
    return UserService.isUserVehicleApproved(user);
  }

  public hasSignedContract(user: User): boolean {
    return (
      !!user?.contractSignature &&
      this.contract?.id === user.contractSignature.contractId
    );
  }

  public isCriminalRecordCheckApproved(user: User): boolean {
    return (
      !!user?.criminalRecordCheck &&
      this.criminalRecordCheckDocument?.id ===
        user.criminalRecordCheck.criminalRecordCheckDocumentId &&
      user.criminalRecordCheck.status === Status.APPROVED
    );
  }

  public hasBankingDetails(user: User): boolean {
    return UserService.areUserBankingDetailsApproved(user);
  }
}
